/**
 * The docking area (SPEC §3 "Default layout"): Outliner | Viewport | Inspector
 * over a bottom dock with the Content Browser and the Console (Statistics
 * tabbed behind it), all dockable, resizable and collapsible through dockview.
 *
 * Layout state is dockview's, not the engine's (A-16: splitter drags aren't
 * commands). It persists to localStorage and is restored only if it still
 * contains exactly the editor's panels (A-20). `panel.focus` requests are
 * applied here, so the command layer never touches dockview.
 */
import { DockviewReact } from "dockview-react";
import type {
  DockviewApi,
  DockviewReadyEvent,
  IDockviewHeaderActionsProps,
  IDockviewPanelHeaderProps,
  IDockviewPanelProps,
  DockviewTheme,
} from "dockview-react";
import "dockview-react/dist/styles/dockview.css";
import { useEffect, useRef } from "react";
import type { FunctionComponent } from "react";
import { useEditor } from "../EditorContext";
import { bottomDockSplits, defaultPanelSizes, isUsableLayout, PANEL_IDS } from "./layout";
import { DockTab, PanelHeaderTab, BottomDockActions } from "./panelTabs";
import { PANELS } from "./panels";
import "./dockview.css";

/** Versioned so an incompatible layout format is simply ignored after an update. */
export const LAYOUT_STORAGE_KEY = "kn.layout.v2";

const THEME: DockviewTheme = { name: "kn", className: "kn-dockview", gap: 0 };

// The panels ignore dockview's props: the dock arranges them, they don't depend on it.
const COMPONENTS: Record<string, FunctionComponent<IDockviewPanelProps>> = Object.fromEntries(
  PANEL_IDS.map((id) => [id, PANELS[id].Component]),
);

const TABS: Record<string, FunctionComponent<IDockviewPanelHeaderProps>> = {
  header: PanelHeaderTab,
  dock: DockTab,
};

export interface DockHostProps {
  /** Receives a function that restores the default layout (Window > Reset Layout). */
  readonly onResetAvailable: (reset: () => void) => void;
}

export function DockHost({ onResetAvailable }: DockHostProps) {
  const { panelFocus } = useEditor();
  const apiRef = useRef<DockviewApi | null>(null);

  const onReady = (event: DockviewReadyEvent) => {
    apiRef.current = event.api;
    if (!restoreLayout(event.api)) buildDefaultLayout(event.api);
    event.api.onDidLayoutChange(() => {
      saveLayout(event.api);
    });
    onResetAvailable(() => {
      event.api.clear();
      buildDefaultLayout(event.api);
    });
  };

  useEffect(
    () =>
      panelFocus.subscribe(() => {
        const id = panelFocus.getSnapshot().panel;
        const panel = id ? apiRef.current?.getPanel(id) : undefined;
        // Only bring forward a panel that's hidden: re-activating one already
        // showing re-attaches its content, which resets its scroll position
        // (the Console's list jumped to the top, drawing no rows).
        if (panel && !panel.api.isVisible) panel.api.setActive();
      }),
    [panelFocus],
  );

  return (
    <DockviewReact
      className="kn-dock"
      theme={THEME}
      components={COMPONENTS}
      tabComponents={TABS}
      rightHeaderActionsComponent={
        BottomDockActions as FunctionComponent<IDockviewHeaderActionsProps>
      }
      onReady={onReady}
      disableFloatingGroups
    />
  );
}

/** The reference layout, sized for the current window (A-20). */
export function buildDefaultLayout(api: DockviewApi): void {
  const sizes = defaultPanelSizes(window.innerWidth, window.innerHeight);

  const viewport = api.addPanel({
    id: "viewport",
    component: "viewport",
    title: PANELS.viewport.title,
  });
  viewport.group.header.hidden = true;

  api.addPanel({
    id: "outliner",
    component: "outliner",
    title: PANELS.outliner.title,
    tabComponent: "header",
    position: { referencePanel: "viewport", direction: "left" },
    initialWidth: sizes.outliner,
  });
  api.addPanel({
    id: "inspector",
    component: "inspector",
    title: PANELS.inspector.title,
    tabComponent: "header",
    position: { referencePanel: "viewport", direction: "right" },
    initialWidth: sizes.inspector,
  });

  // The bottom row spans the full width under all three columns: the Content
  // Browser and the Console side by side, or tabbed together on narrow windows.
  const browser = api.addPanel({
    id: "contentBrowser",
    component: "contentBrowser",
    title: PANELS.contentBrowser.title,
    tabComponent: "dock",
    initialHeight: sizes.dock,
    position: { direction: "below" },
  });
  const split = bottomDockSplits(window.innerWidth);
  api.addPanel({
    id: "console",
    component: "console",
    title: PANELS.console.title,
    tabComponent: "dock",
    inactive: !split,
    position: { referencePanel: "contentBrowser", direction: split ? "right" : "within" },
  });
  // Behind the Console, like Unreal's Statistics under Window: there when wanted, not in the way.
  api.addPanel({
    id: "statistics",
    component: "statistics",
    title: PANELS.statistics.title,
    tabComponent: "dock",
    inactive: true,
    position: { referencePanel: "console", direction: "within" },
  });
  if (split) browser.group.api.setSize({ width: Math.round(window.innerWidth / 2) });
}

function restoreLayout(api: DockviewApi): boolean {
  try {
    const saved: unknown = JSON.parse(window.localStorage.getItem(LAYOUT_STORAGE_KEY) ?? "null");
    if (!isUsableLayout(saved)) return false;
    api.fromJSON(saved as Parameters<DockviewApi["fromJSON"]>[0]);
    return true;
  } catch {
    return false;
  }
}

function saveLayout(api: DockviewApi): void {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(api.toJSON()));
  } catch {
    // Storage may be unavailable (privacy modes); the layout just won't persist.
  }
}
